import { useRef } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

interface ScrollItem {
  image: string;
  title: string;
  location?: string;
  href?: string;
}

interface HorizontalScrollSectionProps {
  items: ScrollItem[];
  snap?: boolean;
}

const HorizontalScrollSection = ({ items, snap = true }: HorizontalScrollSectionProps) => {
  const scrollRef = useRef<HTMLDivElement>(null);

  const scroll = (dir: 1 | -1) => {
    if (!scrollRef.current) return;
    const cardWidth = scrollRef.current.firstElementChild?.clientWidth ?? 360;
    scrollRef.current.scrollBy({ left: dir * (cardWidth + 24), behavior: "smooth" });
  };

  return (
    <div className="relative group/scroll">
      {/* Scroll track */}
      <div
        ref={scrollRef}
        className={`flex gap-6 overflow-x-auto pb-4 scrollbar-hide -mx-5 px-5 md:-mx-12 md:px-12 ${
          snap ? "snap-x snap-mandatory" : ""
        }`}
        style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
      >
        {items.map((item, i) => (
          <a
            key={i}
            href={item.href || "#"}
            className={`group flex-shrink-0 relative overflow-hidden ${
              snap ? "snap-start" : ""
            }`}
            style={{ width: "min(85vw, 420px)" }}
          >
            <div className="relative aspect-[3/4] overflow-hidden">
              <img
                src={item.image}
                alt={item.title}
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                loading="lazy"
              />
              {/* Gradient overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-rich-black/70 via-rich-black/20 to-transparent" />

              {/* Text overlay */}
              <div className="absolute bottom-0 left-0 right-0 p-5 md:p-6">
                <h3 className="font-headline text-xl md:text-2xl text-cream leading-tight mb-1">
                  {item.title}
                </h3>
                {item.location && (
                  <p className="font-body text-xs uppercase tracking-widest text-cream/60">
                    {item.location}
                  </p>
                )}
              </div>
            </div>
          </a>
        ))}
      </div>

      {/* Nav arrows */}
      <button
        onClick={() => scroll(-1)}
        aria-label="Scroll left"
        className="absolute left-2 md:left-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full border border-border bg-card/90 backdrop-blur-sm flex items-center justify-center text-foreground/60 hover:text-foreground hover:border-accent/40 transition-all opacity-0 group-hover/scroll:opacity-100"
      >
        <ChevronLeft className="w-4 h-4" strokeWidth={1.5} />
      </button>
      <button
        onClick={() => scroll(1)}
        aria-label="Scroll right"
        className="absolute right-2 md:right-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full border border-border bg-card/90 backdrop-blur-sm flex items-center justify-center text-foreground/60 hover:text-foreground hover:border-accent/40 transition-all opacity-0 group-hover/scroll:opacity-100"
      >
        <ChevronRight className="w-4 h-4" strokeWidth={1.5} />
      </button>
    </div>
  );
};

export default HorizontalScrollSection;
